import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EnrichmentJob } from "wasp/entities";
import { analytics } from "../analytics";
import { UpdateField } from "../types/api";

type EnrichedProducts = "all" | "filtersOnly" | string[];

export type EnrichmentPendingChange = {
  enrichmentJobId: string;
  productId: string;
  fieldName: string;
  fieldValue: string;
  sourceId?: string;
}

export interface FlowState {
  isGetStartedDialogOpen: boolean;
  isAddStoreDialogOpen: boolean;
  isSwitchStoreDialogOpen: boolean;
  reconnectDialogOpen: boolean;
  isEnrichmentDialogOpen: boolean;
  isReimportExcelDialogOpen: boolean;
  enrichedProducts: EnrichedProducts;
  enrichmentSelectedFeatures: string[] | null;
  shopName: string | null;
  lastUnsyncedJob: EnrichmentJob | null;
  enrichmentPendingChanges: EnrichmentPendingChange[];
  importShopifyInitialLoading: boolean
  fetchingProducts: boolean
  lastFilteredProductCount: number | null
  toastErrors: {
    id: string;
    message: string;
  }[]
}

const initialState: FlowState = {
  importShopifyInitialLoading: false,
  lastFilteredProductCount: null,
  fetchingProducts: false,
  isSwitchStoreDialogOpen: false,
  isAddStoreDialogOpen: false,
  reconnectDialogOpen: false,
  isEnrichmentDialogOpen: false,
  isReimportExcelDialogOpen: false,
  isGetStartedDialogOpen: false,
  shopName: null,
  enrichedProducts: "all",
  lastUnsyncedJob: null,
  enrichmentSelectedFeatures: null,
  enrichmentPendingChanges: [],
  toastErrors: [],
};

export const flowSlice = createSlice({
  name: "flow",
  initialState,
  reducers: {
    setIsAddStoreDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isAddStoreDialogOpen = action.payload;
    },
    setSwitchStoreDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSwitchStoreDialogOpen = action.payload;
    },
    setImportShopifyInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.importShopifyInitialLoading = action.payload;
    },
    setIsGetStartedDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isGetStartedDialogOpen = action.payload;
    },
    setReconnectDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.reconnectDialogOpen = action.payload;
    },
    setReimportExcelDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isReimportExcelDialogOpen = action.payload;
    },
    setToastError: (state, action: PayloadAction<{message: string }>) => {
      const id = crypto.randomUUID();
      state.toastErrors.push({...action.payload, id});
    },
    removeToastError: (state, action: PayloadAction<string>) => {
      state.toastErrors = state.toastErrors.filter(error => error.id !== action.payload);
    },  
    setEnrichmentDialogOpen: (state, action: PayloadAction<{
      isOpen: boolean;
      enrichedProducts?: EnrichedProducts;
      selectedFeatures?: string[];
      isImageCTA?: boolean;
    }>) => {
      state.isEnrichmentDialogOpen = action.payload.isOpen;
      if(action.payload.isOpen){
        const email = localStorage.getItem("email") || "unknown";
        analytics.sendEnhancementMenuOpenedEvent({
          email: email
        })
      }

      if(!action.payload.isOpen){
        state.enrichedProducts = "all";
        state.enrichmentSelectedFeatures = null;
        return;
      }
      

      if(action.payload.isImageCTA){
      }

      state.enrichedProducts = action.payload.enrichedProducts || "all";
      if(action.payload.selectedFeatures) {
        state.enrichmentSelectedFeatures = action.payload.selectedFeatures;

      }else{
        state.enrichmentSelectedFeatures = null;
      }
    },

    setShopName: (state, action: PayloadAction<string | null>) => {
      state.shopName = action.payload;
    },

    setFetchingProducts: (state, action: PayloadAction<boolean>) => {
      state.fetchingProducts = action.payload;
    },

    setLastFilteredProductCount: (state, action: PayloadAction<number>) => {
      state.lastFilteredProductCount = action.payload;
    },

    setLastUnsyncedJob: (state, action: PayloadAction<EnrichmentJob | null>) => {
      state.lastUnsyncedJob = action.payload;
    },

    initializePendingChanges: (state, action: PayloadAction<EnrichmentPendingChange[]>) => {
      state.enrichmentPendingChanges = action.payload;
    },

    addPendingChange: (state, action: PayloadAction<EnrichmentPendingChange>) => {
      const existingChange = state.enrichmentPendingChanges.find(change => {
        return change.enrichmentJobId === action.payload.enrichmentJobId &&
          change.productId === action.payload.productId &&
          change.fieldName === action.payload.fieldName &&
          (!action.payload.sourceId || change.sourceId === action.payload.sourceId);
      });

      if(existingChange){
        existingChange.fieldValue = action.payload.fieldValue;
      }else{
        state.enrichmentPendingChanges.push(action.payload);
      }
    },
    resetPendingChanges: (state) => {
      state.enrichmentPendingChanges = [];
    },

    removePendingChange: (state, action: PayloadAction<{
      enrichmentJobId: string;
      productId: string;
      fieldName: string;
    }>) => {
      state.enrichmentPendingChanges = state.enrichmentPendingChanges.filter(change => {
        return change.enrichmentJobId !== action.payload.enrichmentJobId ||
          change.productId !== action.payload.productId ||
          change.fieldName !== action.payload.fieldName;
      });
    },

    removeAllPendingChangesOfJob: (state, action: PayloadAction<string>) => {
      state.enrichmentPendingChanges = state.enrichmentPendingChanges.filter(change => {
        return change.enrichmentJobId !== action.payload;
      });
    }
  },
});

export const {setLastFilteredProductCount, setFetchingProducts, setSwitchStoreDialogOpen, setIsAddStoreDialogOpen, setIsGetStartedDialogOpen, setImportShopifyInitialLoading, setEnrichmentDialogOpen, setShopName, setReconnectDialogOpen, setLastUnsyncedJob } = flowSlice.actions;
export const { addPendingChange, setReimportExcelDialogOpen, resetPendingChanges, removePendingChange, removeAllPendingChangesOfJob, initializePendingChanges } = flowSlice.actions;
export const { setToastError, removeToastError } = flowSlice.actions

export default flowSlice.reducer;